/**
 * @file PropertiesDocument.js
 * @description Component for managing document properties within a portal page.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FileDrop } from '../helpers/FileDrop';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import { uploadFile } from '../../services/containerService';
import { useUploadContext } from '../../store/Contexts';
import Toast from '../ui/toasts/Toast';
import { formatFileSize } from '../helpers/FileUtils';
import {
    createAsset,
    fetchAssets,
    fetchAssetByID,
} from '../../services/clientService';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import PaginationComponent from '../helpers/PaginationComponent';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { getText } from '../../i18n';

const PropertiesDocument = ({
    item,
    setItem,
    setUploadFile,
    handleUploadFromDevice,
    handleAddFromAssets,
    setIsSaving,
}) => {
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [areInputsDisabled, setAreInputsDisabled] = useState(false);
    const [captionCharacterLimitLabel, setCaptionCharacterLimitLabel] =
        useState('Character Limit 0/200');
    const [captionCharacterLimitStatus, setCaptionCharacterLimitStatus] =
        useState('rgba(47, 43, 67, 0.6)');
    const [documentUrl, setDocumentUrl] = useState('');
    const [caption, setCaption] = useState('');
    const [file, setFile] = useState();
    const { enqueueUpload } = useUploadContext();
    const [assetName, setAssetName] = useState('');
    const [isExternal, setIsExternal] = useState(true);
    const [asset, setAsset] = useState({});
    const [documentAsset, setDocumentAsset] = useState({});

    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [activeCard, setActiveCard] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const [isSearching, setIsSearching] = useState(false);

    const latestRequestIdRef = useRef(0);

    useEffect(() => {
        setItem(prevState => {
            const { documentPart } = prevState;

            return {
                ...prevState,
                documentPart: {
                    ...documentPart,
                    url: '',
                    caption: caption,
                    isExternal: isExternal,
                },
                containerAssets: [documentAsset],
            };
        });
    }, [documentAsset, caption, isExternal]);

    useEffect(() => {
        if (item !== undefined) {
            if (item.hasOwnProperty('templateStatus')) {
                if (item.templateStatus === -1) {
                    setAreInputsDisabled(true);
                }
            }
        }
    }, []);

    const handleCaptionChange = event => {
        handleCaptionCharacterLimit(event);
        setCaption(event.target.value);
    };

    const handleCaptionCharacterLimit = event => {
        var str = event.target.value.toString();
        if (str.length > 200) {
            setCaptionCharacterLimitStatus('rgba(239, 78, 69, 1)');
        } else {
            setCaptionCharacterLimitStatus('rgba(47, 43, 67, 0.6)');
        }
        setCaptionCharacterLimitLabel('Character Limit ' + str.length + '/200');
    };

    const handleURLChange = event => {
        setDocumentUrl(event.target.value);
    };

    useEffect(() => {
        if (item.documentPart && item.documentPart.url) {
            setDocumentUrl(item.documentPart.url);
        } else {
            setDocumentUrl('');
        }

        if (item.documentPart && item.documentPart.caption) {
            setCaption(item.documentPart.caption);
        } else {
            setCaption('');
        }
    }, [item.data]);

    const handleFileChange = fileValue => {
        setFile(fileValue);
    };

    function updateAsset(assetObj, key, value) {
        return { ...assetObj, [key]: value };
    }

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 4; //ContentInProgress
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                setDocumentAsset(prev => ({
                    ...prev,
                    assetId: createResponse.data.id,
                }));
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setAssetName(createResponse.data.name);
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    const loadAssets = useCallback(
        async (page = currentPage, search = searchTerm) => {
            latestRequestIdRef.current += 1;
            const requestId = latestRequestIdRef.current;

            setIsSearching(true);
            setIsSaving(true);

            try {
                let queryParams = {
                    assetTypes: [1, 2],
                    page: page,
                    pageSize: 12,
                    fileTypes: ['docx', 'doc', 'txt', 'pdf'],
                    isMyAsset: false,
                };

                if (search && search.trim() !== '') {
                    queryParams = { ...queryParams, search: search.trim() };
                }

                const fetchedAssets = await fetchAssets(
                    queryParams,
                    null,
                    false,
                );

                if (requestId === latestRequestIdRef.current) {
                    if (!fetchedAssets) {
                        setAssets([]);
                        setTotalPages(1);
                    } else {
                        setAssets(fetchedAssets.viewModels || []);
                        setTotalPages(fetchedAssets.pageTotal || 1);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch assets', error);
                Toast.error(getText('msg_cade149a85b2'));
            } finally {
                if (latestRequestIdRef.current === requestId) {
                    setIsSearching(false);
                    setIsSaving(false);
                }
            }
        },
        [currentPage, searchTerm],
    );

    const debouncedLoadRef = useRef(
        debounce(term => {
            setCurrentPage(1);
            loadAssets(1, term);
        }, 400),
    );

    useEffect(() => {
        const debounced = debouncedLoadRef.current;
        return () => {
            debounced.cancel();
        };
    }, []);

    const handleSearch = event => {
        const value = event.target.value;
        setSearchTerm(value);

        debouncedLoadRef.current(value);

        if (value === '') {
            debouncedLoadRef.current.cancel();
            setCurrentPage(1);
            loadAssets(1, '');
        }
    };

    const handleSearchKeyDown = event => {
        if (event.key === 'Enter') {
            const debounced = debouncedLoadRef.current;
            debounced.cancel();
            setCurrentPage(1);
            loadAssets(1, searchTerm);
        }
    };

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    const addAsset = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];
        setDocumentAsset(prev => ({
            ...prev,
            assetId: assetAdding.id,
        }));
        setAssetName(assetAdding.name);
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    useEffect(() => {
        handleUploadFromDevice(isUploadFromDeviceChecked);
    }, [isUploadFromDeviceChecked]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
        handleAddFromAssets(isAddFromAssetsChecked);
    }, [isAddFromAssetsChecked]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets(currentPage, searchTerm);
        }
    }, [currentPage]);

    useEffect(() => {
        const fetchAssetsInit = async () => {
            if (item !== undefined) {
                if (
                    item.hasOwnProperty('containerAssets') &&
                    item.containerAssets !== null
                ) {
                    item.containerAssets = item.containerAssets.filter(
                        asset => Object.keys(asset).length > 0,
                    );
                    let tempDocumentAsset = {};
                    let tempAssetName = '';

                    for (const containerAsset of item.containerAssets) {
                        if (containerAsset.assetId) {
                            const fetchedAsset = await fetchAssetByID(
                                containerAsset.assetId,
                                null,
                            );
                            containerAsset.url = fetchedAsset.url;
                            tempDocumentAsset = {
                                id: containerAsset.id,
                                assetId: containerAsset.assetId,
                            };
                            tempAssetName = fetchedAsset.name;
                        }
                    }

                    setDocumentAsset(tempDocumentAsset);
                    setAssetName(tempAssetName);
                }
            }
        };

        fetchAssetsInit();

        return () => {
            // Cleanup logic if necessary
        };
    }, []);

    return (
        <>
            <div
                style={{
                    width: '100%',
                    minWidth: '100%',
                    margin: 'auto',
                    paddingTop: '26px',
                    display:
                        isUploadFromDeviceChecked || isAddFromAssetsChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div
                    className="d-flex justify-content-center mb-4"
                    style={{ flexDirection: 'column', gap: '24px' }}
                >
                    <button
                        style={{ width: '100%' }}
                        className={`button-base ing-button-primary-fit-content`}
                        onClick={() => {
                            setIsAddFromAssetsChecked('document');
                            setIsUploadFromDeviceChecked('');
                        }}
                    >
                        {getText('msg_27eb0a10db5c')}
                    </button>
                    <button
                        style={{ width: '100%' }}
                        className={`button-base button-secondary`}
                        onClick={() => {
                            setIsUploadFromDeviceChecked('document');
                            setIsAddFromAssetsChecked('');
                        }}
                    >
                        {getText('msg_c002fe6df29c')}
                    </button>
                </div>
                {assetName && (
                    <div className="form-file-drop">
                        <div className="form-file-dropped">
                            <div className="form-file-dropped-status">
                                {getText('msg_b039895c3ee1')}
                            </div>
                            <div className="form-file-dropped-filename">
                                {assetName ?? ''}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div
                style={{
                    width: '100%',
                    minWidth: '100%',
                    margin: 'auto',
                    paddingTop: '26px',
                    display:
                        isUploadFromDeviceChecked || isAddFromAssetsChecked
                            ? 'none'
                            : 'block',
                }}
            >
                <div className="file-form-label">{getText('msg_3428d97e6666')}</div>
                <textarea
                    onChange={handleCaptionChange}
                    defaultValue={caption}
                    className="form-control"
                    placeholder={getText('msg_e01cc754f786')}
                    style={{
                        height: '100px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        resize: 'none',
                        padding: '16px',
                    }}
                    disabled={areInputsDisabled}
                ></textarea>
                <div
                    style={{
                        fontSize: '14px',
                        fontWeight: '400',
                        textAlign: 'left',
                        color: captionCharacterLimitStatus,
                        marginLeft: '8px',
                        marginTop: '8px',
                    }}
                >
                    {captionCharacterLimitLabel}
                </div>
            </div>

            {/* Upload From Device */}
            {isUploadFromDeviceChecked && (
                <>
                    <div style={{ width: '100%', paddingTop: '6px' }}>
                        <FileDrop file={file} onChange={handleFileChange} />
                    </div>

                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value =>
                            setAsset(updateAsset(asset, 'name', value))
                        }
                        maxChars={256}
                        required={true}
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />

                    <div style={{ marginBottom: '24px' }}>
                        <div className="file-form-label">
                            {getText('msg_848eed0fbd54')}
                            <Tooltip
                                html="Use relevant and specific keywords to help make searching for an asset easier."
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                ></i>
                            </Tooltip>
                        </div>
                        <TagInput
                            onTagsChange={tags =>
                                setAsset(updateAsset(asset, 'assetTags', tags))
                            }
                            savedTags={asset.tags}
                        />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <SwitchField
                            label={getText('msg_0c359a10ae17')}
                            onChange={isChecked =>
                                setAsset(
                                    updateAsset(
                                        asset,
                                        'assetTypeId',
                                        isChecked ? 4 : 5,
                                    ),
                                )
                            }
                            tooltip={getText('msg_77453f9dd036')}
                            checked={asset.assetTypeId !== 5} //UnassignedInProgress
                        />
                    </div>

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isUploadFromDeviceChecked
                                ? 'block'
                                : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsUploadFromDeviceChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => uploadAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked && (
                <>
                    <div
                        className={styles.searchWrapper}
                        style={{ marginBottom: '26px' }}
                    >
                        <input
                            type="text"
                            className={styles.searchInput}
                            style={{ width: '100%' }}
                            placeholder={getText('msg_b18cdf8f03e9')}
                            value={searchTerm}
                            onChange={handleSearch}
                            onKeyDown={handleSearchKeyDown}
                        />
                        <span className={styles.searchIcon}>
                            <i className="bi bi-search"></i>
                        </span>
                    </div>

                    {isSearching && (
                        <div style={{ marginBottom: 12 }}>
                            <small>{getText('msg_1a6a5ba8c23d')}</small>
                        </div>
                    )}

                    <AssetGridView
                        assets={assets}
                        propertiesModel={true}
                        handleClick={handleClick}
                        handleDoubleClick={() => null}
                        activeCard={activeCard}
                        fullAssetName={false}
                    />

                    {assets.length < 1 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            title={
                                searchTerm
                                    ? 'Sorry, no results found'
                                    : 'Sorry, there are no assets to display'
                            }
                            description={
                                searchTerm
                                    ? 'Try adjusting your search to find what you are looking for'
                                    : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                            }
                        />
                    )}

                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={page => setCurrentPage(page)}
                    />

                    <div
                        id="footer"
                        style={{
                            height: 'fit-content',
                            width: '100%',
                            padding: '28px',
                            display: isAddFromAssetsChecked ? 'block' : 'none',
                        }}
                    >
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-start button-base button-secondary"
                            onClick={() => setIsAddFromAssetsChecked('')}
                        >
                            {getText('msg_77dfd2135f4d')}
                        </div>
                        <div
                            type="button"
                            style={{
                                height: '40px',
                                width: '128px',
                                textAlign: 'center',
                                display: 'inline-block',
                            }}
                            className="float-end button-base ing-button-primary-left"
                            onClick={() => addAsset()}
                        >
                            {getText('msg_61cc55aa0453')}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default PropertiesDocument;
